import { useEffect, useState } from "react";
import { business } from "@/lib/business";

const MIN_MS = 750;
const MAX_MS = 1600;

interface Props {
  onDone?: () => void;
}

/**
 * Brand splash shown while the first paint settles.
 *
 * Design: the logo sits on a white card (the logo artwork has a white
 * ground, so it needs one), framed by a rotating conic ring in the brand
 * green/maroon, over a deep green-charcoal field with a soft vignette.
 * A determinate-looking progress rail and the company name anchor the
 * bottom so the screen reads as branded rather than as a generic spinner.
 */
const PremiumLoader = ({ onDone }: Props) => {
  const [hiding, setHiding] = useState(false);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    const start = performance.now();
    let hideTimer: ReturnType<typeof setTimeout>;
    let removeTimer: ReturnType<typeof setTimeout>;

    const hide = () => {
      const wait = Math.max(MIN_MS - (performance.now() - start), 0);
      hideTimer = setTimeout(() => {
        setHiding(true);
        removeTimer = setTimeout(() => {
          setRemoved(true);
          onDone?.();
        }, 550);
      }, wait);
    };

    const maxTimer = setTimeout(hide, MAX_MS);
    if (document.readyState === "complete") {
      hide();
    } else {
      window.addEventListener("load", hide, { once: true });
    }

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
      clearTimeout(maxTimer);
      window.removeEventListener("load", hide);
    };
  }, [onDone]);

  if (removed) return null;

  return (
    <div
      aria-hidden={hiding}
      role="status"
      aria-live="polite"
      aria-label="Loading NIMAR Landscaping & Masonry"
      className={`fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden transition-[opacity,transform] duration-500 ease-out ${
        hiding ? "opacity-0 scale-[1.03] pointer-events-none" : "opacity-100 scale-100"
      }`}
      style={{
        background:
          "radial-gradient(70% 60% at 50% 38%, hsl(100 20% 14%) 0%, hsl(var(--accent)) 45%, hsl(var(--accent-dark)) 100%)",
      }}
    >
      {/* Ambient brand glows */}
      <div
        aria-hidden="true"
        className="absolute -top-40 -left-32 h-[32rem] w-[32rem] rounded-full blur-3xl opacity-40 motion-safe:animate-[loader-drift_9s_ease-in-out_infinite]"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.55), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-30 motion-safe:animate-[loader-drift_11s_ease-in-out_infinite_reverse]"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.5), transparent 70%)" }}
      />
      {/* Faint brick-grid nod to the logo mark */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(0 0% 100% / 0.6) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.6) 1px, transparent 1px)",
          backgroundSize: "72px 36px",
          maskImage: "radial-gradient(ellipse at center, black 20%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 20%, transparent 70%)",
        }}
      />

      <div className="relative flex flex-col items-center px-6">
        {/* Rotating conic ring around the logo card */}
        <div className="relative h-[164px] w-[164px] flex items-center justify-center">
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-[34px] motion-safe:animate-[loader-spin_2.6s_linear_infinite]"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0deg, hsl(var(--primary-bright)) 90deg, hsl(var(--secondary-bright)) 170deg, transparent 250deg)",
              WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0)",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0)",
              filter: "blur(0.3px)",
            }}
          />
          <span
            aria-hidden="true"
            className="absolute inset-[10px] rounded-[26px] border border-white/10"
          />
          <div
            className="relative flex items-center justify-center rounded-[22px] bg-white px-5 py-4 motion-safe:animate-[loader-rise_600ms_cubic-bezier(0.33,1,0.68,1)_both]"
            style={{ boxShadow: "0 24px 60px -18px hsl(100 40% 3% / 0.7)" }}
          >
            <img
              src="/logo.png"
              alt=""
              aria-hidden="true"
              width={167}
              height={95}
              className="h-[86px] w-auto"
            />
          </div>
        </div>

        {/* Company name */}
        <p className="relative mt-8 font-heading text-base sm:text-lg font-bold tracking-[0.22em] uppercase text-white/95 text-center">
          {business.shortName}
          <span className="ml-2 font-normal tracking-[0.18em] text-[hsl(var(--primary-bright))]">
            Landscaping &amp; Masonry
          </span>
        </p>

        {/* Progress rail */}
        <div className="relative mt-6 h-[3px] w-52 overflow-hidden rounded-full bg-white/12">
          <div
            className="absolute inset-y-0 left-0 w-2/5 rounded-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(var(--primary-bright)) 40%, hsl(var(--secondary-bright)) 75%, transparent)",
              animation: "loader-bar 1.25s cubic-bezier(0.65, 0, 0.35, 1) infinite",
            }}
          />
        </div>

        <p className="relative mt-4 text-[11px] tracking-[0.28em] uppercase text-white/60">
          Wakefield, Massachusetts
        </p>
      </div>

      <style>{`
        @keyframes loader-bar {
          0%   { transform: translateX(-110%); }
          100% { transform: translateX(360%); }
        }
        @keyframes loader-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes loader-rise {
          from { opacity: 0; transform: translateY(10px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes loader-drift {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50%      { transform: translate3d(24px, -18px, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [role="status"] * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
};

export default PremiumLoader;
